import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { copilotAsk, agentActions, agentExecute } from "@/lib/campusApi";
import { Sparkles, X, ArrowRight, Send, Play, Check } from "lucide-react";

/* Floating AI copilot — available on every placement-cell page. Opens a
   full-height RIGHT-side drawer grounded in the college's live season data.
   Positioning/sizing is inline-styled on purpose: it must never depend on
   Tailwind JIT having scanned this file. */

const SUGGESTIONS = [
  "Who are my weakest students right now?",
  "Nudge all students who haven't started",
  "Draft interventions for the whole batch",
  "Summarize batch readiness for management",
];

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 px-4 py-3 rounded-2xl rounded-tl-md bg-white/10 border border-white/10 w-fit">
      {[0, 1, 2].map((i) => (
        <span key={i} className="w-1.5 h-1.5 rounded-full bg-amber-300/80"
          style={{ animation: `wshDot 1.2s ${i * 0.18}s infinite ease-in-out` }} />
      ))}
    </div>
  );
}

function AiAvatar() {
  return (
    <span className="shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-amber-300 to-orange-500 text-stone-900 flex items-center justify-center shadow-lg shadow-amber-500/30">
      <Sparkles size={13} />
    </span>
  );
}

export default function CopilotDock() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(0);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    agentActions().then(({ data }) => setPending((data.proposed || []).length)).catch(() => {});
  }, [location.pathname]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 260); }, [open]);

  const ask = async (e, preset) => {
    e?.preventDefault();
    const question = (preset ?? q).trim();
    if (!question || busy) return;
    setMsgs((m) => [...m, { role: "user", text: question }]);
    setQ(""); setBusy(true);
    try {
      const { data } = await copilotAsk(question);
      setMsgs((m) => [...m, { role: "assistant", text: data.answer, action: data.action || null }]);
    } catch (err) {
      setMsgs((m) => [...m, { role: "assistant", text: err?.response?.data?.detail || "Couldn't answer that — try again." }]);
    } finally { setBusy(false); }
  };

  // Run a copilot-proposed action after the TPO confirms.
  const runAction = async (msgIndex, action) => {
    setMsgs((m) => m.map((x, i) => i === msgIndex ? { ...x, actionState: "running" } : x));
    try {
      const { data } = await agentExecute(action.type, action.params);
      setMsgs((m) => [
        ...m.map((x, i) => i === msgIndex ? { ...x, actionState: "done" } : x),
        { role: "assistant", text: `✅ ${data.result}` },
      ]);
      agentActions().then(({ data: a }) => setPending((a.proposed || []).length)).catch(() => {});
    } catch (err) {
      setMsgs((m) => [
        ...m.map((x, i) => i === msgIndex ? { ...x, actionState: null } : x),
        { role: "assistant", text: err?.response?.data?.detail || "That action failed — try again." },
      ]);
    }
  };

  const dismissAction = (msgIndex) => {
    setMsgs((m) => m.map((x, i) => i === msgIndex ? { ...x, actionState: "dismissed" } : x));
  };

  return (
    <>
      <style>{`
        @keyframes wshDot { 0%, 60%, 100% { transform: translateY(0); opacity: .45; } 30% { transform: translateY(-4px); opacity: 1; } }
        @keyframes wshDrawerIn { from { transform: translateX(60px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes wshFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes wshPing { 0% { transform: scale(1); opacity: .5; } 80%, 100% { transform: scale(1.7); opacity: 0; } }
      `}</style>

      {/* Launcher — bottom right, always */}
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="copilot-dock-btn" aria-label="Ask your AI placement officer"
          className="group" style={{ position: "fixed", bottom: 24, right: 24, zIndex: 60 }}>
          <span className="relative flex items-center gap-2.5 pl-2 pr-5 py-2 rounded-full bg-stone-900/95 backdrop-blur border border-white/10 shadow-[0_18px_45px_-12px_rgba(28,25,23,0.6)] group-hover:-translate-y-0.5 group-hover:shadow-[0_22px_55px_-12px_rgba(194,65,12,0.45)] transition-all">
            <span className="relative w-9 h-9 rounded-full bg-gradient-to-br from-amber-300 to-orange-500 text-stone-900 flex items-center justify-center">
              {pending > 0 && <span className="absolute inset-0 rounded-full bg-amber-400" style={{ animation: "wshPing 2.2s infinite" }} />}
              <Sparkles size={17} className="relative" />
            </span>
            <span className="text-left leading-tight">
              <span className="block text-[13px] font-semibold text-stone-50">AI Officer</span>
              <span className="block text-[10px] text-stone-400">
                {pending > 0 ? `${pending} action${pending !== 1 ? "s" : ""} waiting` : "Ask anything"}
              </span>
            </span>
            {pending > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#C2410C] text-white text-[10px] font-bold flex items-center justify-center">{pending}</span>
            )}
          </span>
        </button>
      )}

      {/* Right-side drawer */}
      {open && (
        <div style={{ position: "fixed", inset: 0, zIndex: 70 }}>
          {/* Backdrop */}
          <div onClick={() => setOpen(false)}
            style={{ position: "absolute", inset: 0, background: "rgba(12,10,9,0.45)", backdropFilter: "blur(2px)", animation: "wshFadeIn .2s ease-out" }} />
          {/* Drawer pinned to the right edge, full height */}
          <div
            className="bg-stone-900 text-stone-50 flex flex-col wsh-grain"
            style={{
              position: "absolute", top: 0, right: 0, bottom: 0,
              width: "min(430px, 100vw)", height: "100%",
              borderLeft: "1px solid rgba(255,255,255,0.1)",
              boxShadow: "-24px 0 60px -20px rgba(0,0,0,0.55)",
              animation: "wshDrawerIn .26s cubic-bezier(.21,1.02,.73,1)",
              overflow: "hidden",
            }}
            data-testid="copilot-dock-panel"
          >
            <div style={{ position: "absolute", inset: 0, opacity: 0.45, pointerEvents: "none", background: "radial-gradient(420px 240px at 88% 0%, rgba(217,119,6,0.38), transparent 60%), radial-gradient(300px 200px at 0% 100%, rgba(124,58,237,0.18), transparent 60%)" }} />

            {/* Header */}
            <div className="relative flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-300 to-orange-500 text-stone-900 flex items-center justify-center shadow-lg shadow-amber-500/25"><Sparkles size={18} /></span>
                <div>
                  <p className="text-sm font-semibold">Your AI Placement Officer</p>
                  <p className="text-[11px] text-stone-400 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Live on your season data
                  </p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="copilot-dock-close"
                className="p-2 rounded-xl text-stone-400 hover:bg-white/10 hover:text-stone-200 transition-colors"><X size={17} /></button>
            </div>

            {pending > 0 && (
              <Link to="/campus/ai" onClick={() => setOpen(false)}
                className="relative mx-4 mt-3 flex items-center justify-between px-4 py-2.5 rounded-2xl bg-amber-300/15 border border-amber-300/30 text-amber-300 text-xs font-medium hover:bg-amber-300/25 transition-colors">
                <span className="flex items-center gap-2"><Sparkles size={12} /> {pending} drafted action{pending !== 1 ? "s" : ""} waiting for approval</span>
                <ArrowRight size={13} />
              </Link>
            )}

            {/* Messages */}
            <div className="relative flex-1 px-4 py-4 space-y-4" style={{ overflowY: "auto" }}>
              {msgs.length === 0 && !busy && (
                <div className="pt-2">
                  <div className="flex items-start gap-2.5">
                    <AiAvatar />
                    <div className="text-sm rounded-2xl rounded-tl-md px-4 py-3 bg-white/10 border border-white/10 text-stone-200 leading-relaxed">
                      I watch your batch, drives and recruiter pipeline. Ask me anything — or start with one of these:
                    </div>
                  </div>
                  <div className="mt-3 space-y-2 pl-9">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} onClick={(e) => ask(e, s)}
                        className="block w-full text-left text-[13px] px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-stone-300 hover:bg-amber-300/10 hover:border-amber-300/30 hover:text-amber-200 transition-colors">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {msgs.map((m, i) => m.role === "user" ? (
                <div key={i} className="flex justify-end">
                  <div className="text-sm rounded-2xl rounded-br-md px-4 py-2.5 bg-amber-300 text-stone-900 font-medium leading-relaxed whitespace-pre-wrap" style={{ maxWidth: "85%" }}>{m.text}</div>
                </div>
              ) : (
                <div key={i} className="flex items-start gap-2.5">
                  <AiAvatar />
                  <div style={{ maxWidth: "85%" }}>
                    <div className="text-sm rounded-2xl rounded-tl-md px-4 py-3 bg-white/10 border border-white/10 text-stone-200 leading-relaxed whitespace-pre-wrap">{m.text}</div>
                    {/* Proposed action — runs only on explicit confirm */}
                    {m.action && m.actionState !== "dismissed" && m.actionState !== "done" && (
                      <div className="mt-2 rounded-2xl border border-amber-300/40 bg-amber-300/10 px-4 py-3" data-testid="copilot-action-card">
                        <p className="text-[11px] font-mono uppercase tracking-widest text-amber-300/80">Proposed action</p>
                        <p className="text-sm text-amber-100 font-medium mt-1">{m.action.label}</p>
                        <div className="mt-2.5 flex gap-2">
                          <button onClick={() => runAction(i, m.action)} disabled={m.actionState === "running"} data-testid="copilot-action-run"
                            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-amber-300 text-stone-900 text-xs font-semibold hover:bg-amber-200 disabled:opacity-60">
                            {m.actionState === "running" ? "Running…" : <><Play size={11} /> Run it</>}
                          </button>
                          <button onClick={() => dismissAction(i)} disabled={m.actionState === "running"}
                            className="px-4 py-1.5 rounded-full border border-white/20 text-stone-300 text-xs hover:bg-white/10 disabled:opacity-60">
                            Not now
                          </button>
                        </div>
                      </div>
                    )}
                    {m.action && m.actionState === "done" && (
                      <p className="mt-1.5 text-[11px] text-emerald-400 flex items-center gap-1"><Check size={11} /> Action completed</p>
                    )}
                  </div>
                </div>
              ))}
              {busy && <div className="flex items-start gap-2.5"><AiAvatar /><TypingDots /></div>}
              <div ref={endRef} />
            </div>

            {/* Input */}
            <form onSubmit={ask} className="relative px-4 pb-4 pt-3 border-t border-white/10">
              <div className="flex items-center gap-2 rounded-2xl bg-white/10 border border-white/15 focus-within:border-amber-300/60 transition-colors pl-4 pr-1.5 py-1.5">
                <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="copilot-dock-input"
                  placeholder="Ask about students, drives, recruiters…"
                  className="flex-1 bg-transparent text-sm text-stone-50 placeholder:text-stone-500 focus:outline-none py-1.5" />
                <button type="submit" disabled={busy || !q.trim()} aria-label="Send"
                  className="w-9 h-9 rounded-xl bg-amber-300 text-stone-900 flex items-center justify-center hover:bg-amber-200 disabled:opacity-40 transition-colors">
                  <Send size={15} />
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
